'use client';

import { useState, useContext, useMemo, useCallback } from 'react';
import Link from 'next/link';
import styles from './Header.module.css';
import { UserContext } from '@/_contexts/UserProvider';
import {
  Avatar,
  Button,
  ButtonGroup,
  Dialog,
  Dropdown,
  Flex,
  Icon,
  Logo,
  Pill,
  Text,
  GlobalBannerAlert,
  type ResultListItem,
} from '@/_components';
import { QuestionnaireResponsiveNavigation } from './_components/QuestionnaireResponsiveNavigation';
import { getClasses } from '@/_utils/classnames';
import { Notifications } from './_components/Notifications';
import { usePathname, useParams } from 'next/navigation';
import { FIRM_ACCESS_NAME_MAP } from '@/_constants/roles';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { MessagesButton } from './_components/MessagesButton';
import { FirmAccessType } from '@/_types/roles';
import { HelpCenter } from './_components/HelpCenter';
import {
  getSessionStorageItem,
  setLocalStorageItem,
} from '@/_utils/localstorage';
import { PreviewAlert } from './_components/PreviewAlert';
import { MaintenanceBanner } from '@/_components/MaintenanceBanner';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { ChangeLog } from './_components/ChangeLog';
import { SelectedFirmIndicator } from './_components/SelectedFirmIndicator';

export const Header = () => {
  const pathname = usePathname();
  const { previewUserId, previewQuestionnaireId } = useParams();

  const [isOpen, setOpen] = useState<boolean>(false);
  const {
    preferences,
    firmLogoUrl,
    availableFirms,
    firm: selectedFirm,
  } = useContext(FirmSettingsContext);
  const { setIsAssistantOpen, isAssistantOpen } = useContext(LayoutContext);

  const {
    user,
    initials,
    isAdvisor,
    fullName,
    signOut,
    isLoggedIn,
    isSorabanAdmin,
    isUserReadOnly,
  } = useContext(UserContext);

  const DROPDOWN_ITEMS: Array<ResultListItem> = [
    ...(preferences?._tmp_multi_firm_access_enabled
      ? [
          {
            type: 'category',
            name: 'Firms',
          } as ResultListItem,
          ...availableFirms.map(
            (firm) =>
              ({
                name: firm.name,
                icon: 'buildings',
                isDisabled: firm.id === selectedFirm?.id,
                onClick: () => {
                  console.log('TBD', firm.id);
                },
              }) as ResultListItem,
          ),
          { type: 'separator' } as ResultListItem,
        ]
      : []),
    {
      name: 'Sign Out',
      icon: 'key',
      onClick: () => {
        const windowId: string | undefined | null =
          getSessionStorageItem('windowId');

        if (windowId) {
          // Setting this to help prevent an alert from appearing
          setLocalStorageItem('signedOutWindowId', windowId);
        }

        signOut();
      },
    },
  ];

  const isQuestionnairePage: boolean = useMemo(
    () => pathname.startsWith('/questionnaire/'),
    [pathname],
  );
  const onToggleSectionDialog = () => setOpen(!isOpen);

  const headerClasses: string = getClasses(
    styles.header,
    'flex space-between flex-y-center gap-8',
  );

  const logoClasses: string = getClasses('flex flex-y-center', {
    'mobile:hidden': isQuestionnairePage,
    [styles.headerLogo]: isAdvisor,
  });

  const handleAssistantToggle = useCallback(() => {
    setIsAssistantOpen(!isAssistantOpen);
  }, [isAssistantOpen, setIsAssistantOpen]);

  const userTitle =
    user?.firm_access === 'group' && !!user?.firm_group?.name
      ? `${user.firm_group.name} Group`
      : FIRM_ACCESS_NAME_MAP[user?.firm_access as FirmAccessType];

  const aiButtonClasses = getClasses(styles.header__aiButton, {
    [styles.header__aiButtonActive]: isAssistantOpen,
  });

  const hasMultipleFirms: boolean = Boolean(
    !!preferences?._tmp_multi_firm_access_enabled && availableFirms?.length > 1,
  );

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onToggleSectionDialog} title="Sections">
        <Dialog.Content>
          <QuestionnaireResponsiveNavigation onToggle={onToggleSectionDialog} />
        </Dialog.Content>
      </Dialog>
      <header className={headerClasses} data-hide-on-print="true">
        <Flex isYCentered={true} gap={8}>
          {(!!previewUserId || !!previewQuestionnaireId) && <PreviewAlert />}
          <Link href="/" className={logoClasses}>
            {firmLogoUrl ? (
              <img
                src={firmLogoUrl}
                style={{ maxHeight: '40px' }}
                alt="Company Logo"
              />
            ) : (
              <Logo size={16} />
            )}
          </Link>
          <GlobalBannerAlert />
          <MaintenanceBanner />

          {/* <BreadCrumbs /> */}
          <ButtonGroup
            className={isQuestionnairePage ? 'hidden mobile:flex' : 'hidden'}
          >
            <Link href="/">
              <Button
                intent="ghost"
                value="Back"
                isIconOnly={true}
                size="small"
                iconLeft="caret-left"
              />
            </Link>
          </ButtonGroup>
        </Flex>

        <Flex isYCentered={true} gap={6}>
          {isAdvisor && <ChangeLog />}
          <HelpCenter isAdvisor={isAdvisor} />
          <ButtonGroup isSmallGroup={true} isSameLine={true}>
            <Notifications />
            {(isSorabanAdmin || preferences?._tmp_ai_chat_enabled) &&
              preferences?.ai_chat_enabled && (
                <button
                  className={aiButtonClasses}
                  onClick={handleAssistantToggle}
                >
                  <Icon size={20} name="sparkle" />
                  <Text
                    type="small"
                    fontWeight="medium"
                    color="primary-on-color"
                    noWrap={true}
                  >
                    Assistant
                  </Text>
                </button>
              )}
          </ButtonGroup>
          {!isAdvisor && isLoggedIn && <MessagesButton />}
          {isQuestionnairePage && (
            <ButtonGroup className="hidden tablet:flex">
              <Button
                value="Sections"
                onClick={onToggleSectionDialog}
                size="small"
                intent="primary"
                iconRight="list"
              />
            </ButtonGroup>
          )}

          {isLoggedIn && (
            <div data-testid="header-dropdown">
              <Dropdown items={DROPDOWN_ITEMS}>
                <Flex className="cursor-pointer" isYCentered={true} gap={4}>
                  {isUserReadOnly && (
                    <Pill label="Read Only" size="small" intent="warning" />
                  )}
                  <Avatar
                    type="me"
                    size={40}
                    name={fullName}
                    avatarUrl={user?.medium_url}
                    className="mobile:hidden"
                  />
                  <Flex isColumn={true}>
                    <Text type="small" noWrap={true}>
                      <span className="flex mobile:hidden">{fullName!}</span>
                      <span className="hidden mobile:flex">{initials}</span>
                    </Text>
                    {!isAdvisor && hasMultipleFirms && (
                      <SelectedFirmIndicator title={selectedFirm?.name} />
                    )}

                    {isAdvisor && (
                      <Flex isYCentered={true} gap={2}>
                        <Text
                          type="tiny"
                          color="secondary"
                          className="flex mobile:hidden"
                        >
                          {userTitle}
                        </Text>
                        {hasMultipleFirms && (
                          <SelectedFirmIndicator title={selectedFirm?.name} />
                        )}
                      </Flex>
                    )}
                  </Flex>
                  <Icon name="caret-down" size={20} />
                </Flex>
              </Dropdown>
            </div>
          )}
        </Flex>
      </header>
    </>
  );
};
